import React, { useState, useEffect } from 'react';
import { Stack, Box, Typography, TextField, Button, useMediaQuery, useTheme, Alert, Snackbar, FormControlLabel, Checkbox } from "@mui/material";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { DoktorLinearBar } from "./components/DoktorLinearBar";
import { DoktorFooter } from "./components/DoktorFooter";
import { useClientApi } from '../../api/client';
import { CLIENT_TYPES } from '../../api/clientconfig';
import { SimpleLoader } from '../../components/loading/simpleLoader';

const descriptionStrengthLevels = {
	Empty: {
		title: '',
		color: '',
		text: '',
	},
	Low: {
		title: 'Low',
		color: 'red',
		text: "Your description could use a little more detail. Tell us as much as you can about the symptoms you're experiencing.",
	},
	Average: {
		title: 'Good',
		color: '#F2C01B',
		text: 'Good description! If you can provide more details, that would be great. If not, no problem.',
	},
	High: {
		title: 'Excellent',
		color: '#A6E177',
		text: 'This is a very detailed description. All this information makes our work much easier.',
	},
};

export const DoktorSymptomChecker = () => {
	// Add useLocation hook to get URL parameters
	const location = useLocation();
	const [ currentStep, setCurrentStep ] = useState(10);
	const resultsToken = new URLSearchParams( location.search ).get( 'result' );
    const [ isLoading, setIsLoading ] = useState( false );
	// State Management
	const [ showSymptomsInput, setShowSymptomsInput ] = useState( false );
	const [ isShowTNCPage, setIsShowTNCPage ] = useState( false );
	const [ errorMessageOpen, setErrorMessageOpen ] = useState( false );
	const [ experiencedSymptoms, setExperiencedSymptoms ] = useState(
		sessionStorage.getItem( 'symptom-checker-experiencedSymptoms' ) ?? ''
	);
	const [ isServerError, setIsServerError ] = useState( false );
	const [ isSymptomValidated, setIsSymptomValidated ] = useState( true );
	const [ symptomsExist, setSymptomsExist ] = useState(
		sessionStorage.getItem( 'symptom-checker-symptomsExist' )
			? JSON.parse( sessionStorage.getItem( 'symptom-checker-symptomsExist' ) )
			: null
	);
	const [ userData, setUserData ] = useState( {
		checkedAgreementInfo : false,
		checkedTermsAndPrivacy : false
		} );

	useEffect( () => {
		// Set organization ID - in this case we know it's Doktor (ID: 1)
		localStorage.setItem( 'organization_id', '1' );

		// Save results token if present
		if ( resultsToken ) {
			localStorage.setItem( 'results_token', resultsToken );
		}
	}, [ resultsToken ] );


	// Hooks
	const navigate = useNavigate();
	const theme = useTheme();
	const isLaptop = useMediaQuery( theme.breakpoints.up( 'md' ) );
	const isSmallHeight = useMediaQuery('(max-height:700px)');
	const clientApi = useClientApi( CLIENT_TYPES.DOKTOR_CONNECT );

	// Calculate description strength
	const descriptionStrength = React.useMemo( () => {
		if ( experiencedSymptoms.length === 0 ) return descriptionStrengthLevels.Empty;
		if ( experiencedSymptoms.length < 70 ) return descriptionStrengthLevels.Low;
		if ( experiencedSymptoms.length >= 70 && experiencedSymptoms.length <= 1000 ) {
			return descriptionStrengthLevels.Average;
		}
		return descriptionStrengthLevels.High;
	}, [ experiencedSymptoms ] );

	// Session storage management
	useEffect( () => {
		if ( symptomsExist !== null ) {
			sessionStorage.setItem( 'symptom-checker-symptomsExist', symptomsExist );
		}
	}, [ symptomsExist ] );

	useEffect( () => {
		sessionStorage.setItem( 'symptom-checker-experiencedSymptoms', experiencedSymptoms );
	}, [ experiencedSymptoms ] );

	// Handler for submitting empty symptoms
	const handleEmptySymptoms = async () => {
		const sessionId = localStorage.getItem( 'session-id' );
		console.log( sessionId );
		if ( !sessionId ) {
			setIsServerError( true );
			return;
		}
		try {
			setIsLoading(true)
			await clientApi.postSymptoms( sessionId, null, resultsToken );
			// Redirect to t & c page
			setIsLoading(false)
			setIsShowTNCPage(true)
			setShowSymptomsInput(false)
			// navigate( `/results` );
		} catch ( error ) {
			setIsLoading(false)
			console.error( error );
			setIsServerError( true );
		}
	};

	const handleYesClick = () => {
		setCurrentStep(11)
		setSymptomsExist( true );
		setShowSymptomsInput( true );
	};

	const handleNoClick = async () => {
		setCurrentStep(12)
		setSymptomsExist( false );
		await handleEmptySymptoms();
	};

	

	const handleNext = async () => {
		if ( symptomsExist === null ) {
			setErrorMessageOpen( true );
			return;
		}

		if ( !symptomsExist ) {
			await handleEmptySymptoms();
			return;
		}

		const experiencedSymptomsConditions =
			experiencedSymptoms !== '' &&
			experiencedSymptoms.length >= 70 &&
			experiencedSymptoms.length <= 1500;

		if ( experiencedSymptomsConditions ) {
			const sessionId = localStorage.getItem( 'session-id' );
			if ( !sessionId ) {
				setIsServerError( true );
				return;
			}
			setCurrentStep(12)
			try {
				setIsLoading(true)
				const response = await clientApi.postSymptoms(
					sessionId,
					experiencedSymptoms,
					resultsToken
				);
				setIsLoading(false)
				// Store conditions in localStorage if they exist in response
				if ( response.conditions ) {
					localStorage.setItem( 'conditions', JSON.stringify( response.conditions ) );
				}
				
				// Redirect to results
				// navigate( `/results` );
				setIsShowTNCPage(true)
			} catch ( error ) {
				setIsLoading(false)
				console.error( error );
				setIsServerError( true );
			}
		}

		setIsSymptomValidated( experiencedSymptomsConditions );
	};

	const handleSkip = async () => {
		setCurrentStep(12)
		setSymptomsExist( false );
		await handleEmptySymptoms();
	};

	const handleSubmit = () => {
		navigate('/results')
	}

	return (
		<>
		{
		isLoading ? 
		<SimpleLoader textColor="#232A67" loadingText="Loading..."></SimpleLoader>
		:
		<Box
			sx={ {
				height: isLaptop ? '100vh' : ( isSmallHeight ? "85vh" : "90vh" ),
				display: 'flex',
				flexDirection: 'column',
				maxWidth: isLaptop ? '1200px' : '800px',
				margin: '0 auto',
				padding: '16px',
				boxSizing: 'border-box',
			} }>
			<Box className="doktor-symptoms"
				sx={ {
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					maxWidth: isLaptop ? '800px' : '390px',
					width: '100%',
					margin: '0 auto',
					minHeight: isLaptop ? 'auto' : '100%',
				} }
			>
				{/* Header */ }
				<Box className='doktor-header' height={ 40 }>
				<DoktorLinearBar value={ ( currentStep ) * ( 100 / 12) } />
				</Box>

				{/* Body */ }
				<Box className='doktor-body' paddingX={ 0.5 } sx={{
					display:"flex",
					 flexDirection:"column",
					 justifyContent:"center",
					 alignItems : "center",
					 height : "100%"

				}}>
					{/* Initial Symptoms Question */ }
					{ !showSymptomsInput && !isShowTNCPage && (
						<Stack className="symtoms-question-buttons">
							<Typography
								 color="#232A67"
								sx={ {
									fontSize: isLaptop ? '23px': '23px',
									fontWeight: '700',
									lineHeight: '27px',
									textAlign: 'center',
									marginTop: 2
								} }
							>
								Are you experiencing any health symptoms?
							</Typography>

							<Stack className="buttons" marginTop={ 3 }>
								<Button
									sx={ {
										height: '44px',
										background: '#0633CB14',
										borderRadius: '10px',
										transition: "all 0.3s ease",
										"&:hover": {
											backgroundColor: "#0633CB4D",
										},
									} }
									onClick={ handleYesClick }
								>
									<Typography color="#000" sx={ { fontSize: '15px', fontWeight: '500' } }>
										Yes
									</Typography>
								</Button>

								<Button
									sx={ {
										height: '44px',
										background: '#0633CB14',
										marginTop: '1rem',
										borderRadius: '10px',
										transition: "all 0.3s ease",
										"&:hover": {
											backgroundColor: "#0633CB4D",
										},
									} }
									onClick={ handleNoClick }
								>
									<Typography color="#000" sx={ { fontSize: '15px', fontWeight: '500' } }>
										No
									</Typography>
								</Button>
							</Stack>
						</Stack>
					) }

					{/* Symptoms Input */ }
					{ showSymptomsInput && !isShowTNCPage && (
						<Box className="symptoms-input"
							sx={ {
								marginTop: '1rem',
								display: 'flex',
								flexDirection: 'column',
								// minHeight: '400px'
							} }>
							<Stack>
								<Typography
									 color="#232A67"
									sx={ {
										fontSize: isLaptop ? '23px': '23px',
										fontWeight: '700',
										lineHeight: '22px',
										textAlign: 'center',
									} }
								>
									Can you tell us more?
								</Typography>
								<Typography
									color="#000"
									sx={ {
										fontSize: '14px',
										fontWeight: '400',
										lineHeight: '18px',
										textAlign: 'center',
										marginTop: '15px',
									} }
								>
									In as much detail as you can, please tell us about the symptoms you've been experiencing.
								</Typography>

								<Box className='input-field' marginY={isSmallHeight ? 1 : 2}
									sx={ {
										backgroundColor: '#f8f9fc',
										borderRadius: '12px',
										padding: '2px',
										border: '1px solid #e5e7eb',
										boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
										transition: 'all 0.2s ease',
										height: 'auto',
										'&:hover': {
											boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
											borderColor: '#d1d5db'
										},
										'&:focus-within': {
											borderColor: '#0633CB',
											boxShadow: '0 0 0 2px rgba(6, 51, 203, 0.1)'
										}
									} }
								>
									<TextField
										placeholder="'I have been feeling extra tired and bloated lately...'"
										multiline
										rows={ isLaptop ? 6 : 5}
										value={ experiencedSymptoms }
										onChange={ ( e ) => setExperiencedSymptoms( e.target.value ) }
										error={ !isSymptomValidated }
										helperText={
											!isSymptomValidated && experiencedSymptoms === ''
												? 'Please type the symptoms you are experiencing.'
												: ''
										}
										inputProps={ {
											maxLength: 1500,
											style: {
												fontSize: '15px',
												lineHeight: '1.5',
												color: '#374151'
											}
										} }
										sx={ {
											width: '100%',
											'& .MuiOutlinedInput-root': {
												backgroundColor: 'transparent',
												'& fieldset': {
													border: 'none'
												},
												'& textarea': {
													maxHeight: '150px',
													overflowY: 'auto'
												}
											},
											'& .MuiOutlinedInput-input': {
												padding: isLaptop ? '8px' : '2px',
												'&::placeholder': {
													color: '#9ca3af',
													opacity: 1
												}
											},
											'& .MuiFormHelperText-root': {
												marginLeft: '16px'
											}
										} }
									/>
								</Box>

								{ experiencedSymptoms.length > 0 && (
									<Typography variant="body1" pt={ 1 }>
										<span style={ { fontWeight: 'bold' } }>Description strength: </span>
										<span style={ {
											fontWeight: 'bold',
											color: descriptionStrength.color,
										} }>
											{ descriptionStrength.title }
										</span>
										<br />
										<span>{ descriptionStrength.text }</span>
									</Typography>
								) }
							</Stack>

							<Box className='next-skip-btns'
								sx={ {
									marginY: 3,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
								} }>
								<Button
									sx={ {								
										width: isLaptop ? '340px' : '100%',
										height: '57px',
										background: '#0633CB',
										marginTop:  isSmallHeight ? '6px' :'1rem',
										borderRadius: '10px',
										marginX: 'auto',
										'&:hover': {
											background: '#0633CB',
										},
									} }
									onClick={ handleNext }
								>
									<Typography color="#FFF" sx={ { fontSize: '20px', fontWeight: '600' } }>
										Next
									</Typography>
								</Button>

								<Button
									sx={ {
										width: '175px',
										height: '30px',
										background: '#fff',
										borderRadius: '10px',
										marginX: 'auto',
										marginTop: '10px',
									} }
									onClick={ handleSkip }
								>
									<Typography color="#A1A0A0" sx={ { fontSize: '17px', fontWeight: '600' } }>
										No, skip.
									</Typography>
								</Button>
							</Box>
						</Box>
					) }

					{ isShowTNCPage && 
						<Box display="flex" sx={{
							display : "flex",
							flexDirection : "column",
							justifyContent : { xs : "center", sm : "center", md : "flex-start", lg : "flex-start"}
						}}>
					 <FormControlLabel
               			 control={
                    <Checkbox
                        checked={ userData.checkedAgreementInfo }
                        onChange={ ( e ) => setUserData( prev => ( {
                            ...prev,
                            checkedAgreementInfo: e.target.checked
                        } ) ) }
                        sx={ {
                            color: '#000',
                            '&.Mui-checked': {
                                color: '#0633CB',
                            },
                        } }
                    />
                }
                label={
                    <Typography sx={ { fontSize: isLaptop ? '16px' : { xs: '14px', sm: '16px' }, lineHeight: isLaptop ? '24px' : { xs: '18px', sm: '20px' }, textAlign: 'left' } }>
                        {/* I understand the information provided in this assessment is not intended for the diagnosis treatment or prevention of disease. */}
                        I understand the information provided in this assessment is not intended for the diagnosis treatment or prevention of disease, but rather to provide me general information about my health and products that may be able to improve my wellbeing. 
                    </Typography>
                }
                      sx={ { alignItems: 'flex-start', marginBottom: 2 } }
          			  />
   		         <FormControlLabel
          		      control={
                    <Checkbox
                        checked={ userData.checkedTermsAndPrivacy }
                        onChange={ ( e ) => setUserData( prev => ( {
                            ...prev,
                            checkedTermsAndPrivacy: e.target.checked
                        } ) ) }
                        sx={ {
                            color: '#000',
                            '&.Mui-checked': {
                                color: '#0633CB',
                            },
                            padding: '9px', // Add padding to match the first checkbox
                        } }
                    />
                }
                label={
                    <Typography sx={ { fontSize: isLaptop ? '16px' : { xs: '14px', sm: '16px' }, lineHeight: isLaptop ? '24px' : { xs: '18px', sm: '20px' }, textAlign: 'left', marginTop: '8px' } }>
                        I have read and understood the { ' ' }
                        <Link href="/terms-of-use" color="#0633CB" underline="hover">terms of use</Link>
                        { ' ' } and { ' ' }
                        <Link href="/privacy-policy" color="#0633CB" underline="hover">privacy policy</Link>.
                    </Typography>
                }
                sx={ {
                    alignItems: 'flex-start',
                    marginBottom: 2,
                    '.MuiFormControlLabel-label': {
                        marginTop: '9px' // Align text with the first checkbox
                    }
               			 } }
                 />
        		<Box sx={{ 
                display : "flex",
                justifyContent : "center",
                width : "100%"
            }}>
            <Button
                onClick={ handleSubmit }
                disabled={ !userData.checkedAgreementInfo || !userData.checkedTermsAndPrivacy || isLoading }
                sx={ {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
					width: isLaptop ? '340px' : '100%',
                    height: isLaptop ? "64px" : "57px",
                    background: "#0633CB",
                    marginTop: "0.8rem",
                    borderRadius: "10px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        backgroundColor: "#0633CB",
                    },
                    "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                        color: "rgba(0, 0, 0, 0.26)",
                    },
                } }
            >
                <Typography
                    color="#FFF"
                    display="block"
                    sx={ {
                        fontSize: isLaptop ? "22px" : "20px",
                        fontWeight: "600",
                        lineHeight: isLaptop ? "32px" : "30px",
                        textAlign: "center",
                        transition: "all 0.3s ease",
					
                    } }
                >
                    { isLoading ? 'Submitting...' : 'Get My Results!' }
                </Typography>
            </Button>
           			 </Box>
						</Box>
					}
				</Box>

				{/* Footer */ }
				<Box textAlign='center' sx={ { marginTop: 'auto', position: 'relative' } }>
					<DoktorFooter />
				</Box>
			</Box>

			{/* Error Snackbars */ }
			<Snackbar
				open={ errorMessageOpen }
				autoHideDuration={ 5000 }
				onClose={ () => setErrorMessageOpen( false ) }
				anchorOrigin={ {
					vertical: 'bottom',
					horizontal: 'center',
				} }
			>
				<Alert severity="error" sx={ {
					display: 'flex',
					alignItems: 'center',
					textAlign: 'center',
					mb: 8, fontSize: {
						xs: 16,
						sm: 18,
					},
					px: {
						xs: 2,
						sm: 5,
					},
					borderRadius: 3,
				} }>
					Complete the first question
				</Alert>
			</Snackbar>

			<Snackbar
				open={ isServerError }
				autoHideDuration={ 8000 }
				onClose={ () => setIsServerError( false ) }
				anchorOrigin={ {
					vertical: 'bottom',
					horizontal: 'center',
				} }
			>
				<Alert
					variant="filled"
					severity="error"
					sx={ {
						display: 'flex',
						alignItems: 'center',
						textAlign: 'center',
						mb: 8,
						fontSize: {
							xs: 16,
							sm: 18,
						},
						px: {
							xs: 2,
							sm: 5,
						},
						borderRadius: 3,
					} }
				>
					An error occurred. Please ensure you have filled out your symptoms correctly. If the problem persists, please
					try again later.
				</Alert>
			</Snackbar>
		</Box>
		}
		</>
	);
};